import { Info } from 'lucide-react';

const INK = '#c5c8cc';
const PAPER = '#f3f4f6';
const LINE = '#d5d8dc';

function Stars({ x, y, count = 5 }: { x: number; y: number; count?: number }) {
  return (
    <g fill={INK}>
      {Array.from({ length: count }, (_, index) => (
        <polygon
          key={index}
          points="6,0.6 7.4,4.2 11.2,4.4 8.2,6.8 9.1,10.5 6,8.6 2.9,10.5 3.8,6.8 0.8,4.4 4.6,4.2"
          transform={`translate(${x + index * 7} ${y}) scale(0.55)`}
        />
      ))}
    </g>
  );
}

function Shoe({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} fill={INK}>
      <path d="M1 14c.6-6 6-9 13-7 2 .6 4 2.6 7 2.6h8c3.4 0 6.2 1.6 8 4.4l.6 1.6H5.4C3 15.6 1.6 14.4 1 14z" />
      <path d="M2 17.4h34c.4 1.8-.6 3.2-3.4 3.2H5.2C2.6 20.6 1.4 19.2 2 17.4z" />
    </g>
  );
}

function Shirt({ x, y }: { x: number; y: number }) {
  return (
    <path
      transform={`translate(${x} ${y})`}
      fill={INK}
      d="M10 4l6 2 6-2 6 6-4 3v15H8V13L4 10z"
    />
  );
}

function Cap({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`} fill={INK}>
      <path d="M8 16c0-8 4-12 10-12s10 4 10 12H8z" />
      <path d="M4 16h28c1 3-2 5-6 5H10c-4 0-7-2-6-5z" />
    </g>
  );
}

function Lines({ x, y, widths }: { x: number; y: number; widths: number[] }) {
  return (
    <g fill={INK}>
      {widths.map((width, index) => (
        <rect key={index} x={x} y={y + index * 6} width={width} height="3" rx="1.5" />
      ))}
    </g>
  );
}

function CollectionsArt() {
  return (
    <svg viewBox="0 0 168 108" className="w-[168px] h-[108px]" aria-hidden>
      <rect x="6" y="4" width="156" height="100" rx="8" fill="#fff" stroke={LINE} />
      <rect x="16" y="14" width="64" height="42" rx="4" fill={PAPER} />
      <Shoe x={28} y={22} scale={0.85} />
      <Lines x={18} y={62} widths={[58, 46, 36]} />
      <Stars x={18} y={84} />
      <rect x="88" y="14" width="64" height="42" rx="4" fill={PAPER} />
      <Shoe x={100} y={22} scale={0.85} />
      <Lines x={90} y={62} widths={[58, 40]} />
      <Stars x={90} y={84} />
    </svg>
  );
}

function StoreSpotlightArt() {
  return (
    <svg viewBox="0 0 168 108" className="w-[168px] h-[108px]" aria-hidden>
      <Lines x={28} y={8} widths={[112, 72]} />
      {[
        { x: 14, icon: <Shoe x={8} y={16} scale={0.7} /> },
        { x: 64, icon: <Shirt x={10} y={14} /> },
        { x: 114, icon: <Cap x={6} y={16} /> },
      ].map((card) => (
        <g key={card.x}>
          <rect x={card.x} y="28" width="40" height="68" rx="4" fill="#fff" stroke={LINE} />
          {card.icon && <g transform={`translate(${card.x} 28)`}>{card.icon}</g>}
        </g>
      ))}
    </svg>
  );
}

function VideoArt() {
  return (
    <svg viewBox="0 0 168 108" className="w-[168px] h-[108px]" aria-hidden>
      <rect x="18" y="4" width="132" height="100" rx="6" fill="#fff" stroke={LINE} />
      <rect x="28" y="14" width="112" height="58" rx="4" fill={PAPER} />
      <polygon points="74,32 74,54 96,43" fill={INK} />
      <g transform="translate(30 80)">
        <rect width="22" height="16" rx="2" fill={PAPER} />
        <Cap x={-2} y={1} />
      </g>
      <Stars x={58} y={84} count={4} />
      <g transform="translate(112 80)">
        <rect width="22" height="16" rx="2" fill={PAPER} />
        <Shoe x={1} y={1} scale={0.45} />
      </g>
    </svg>
  );
}

const FORMATS = [
  {
    id: 'collections',
    title: 'Collections',
    description: 'Display a collection of products relevant to shopper interests.',
    art: <CollectionsArt />,
    selected: true,
  },
  {
    id: 'store-spotlight',
    title: 'Store spotlight',
    description: 'Promote your product categories or pages within your Brand Store on Amazon.',
    art: <StoreSpotlightArt />,
    selected: false,
  },
  {
    id: 'video',
    title: 'Video',
    description: 'Use video to promote your brand and products.',
    art: <VideoArt />,
    selected: false,
  },
] as const;

export function SbAdFormatCards() {
  return (
    <div>
      <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-foreground">
        Ad format
        <Info className="h-3.5 w-3.5 text-foreground-muted" aria-hidden />
        <span className="sr-only">Collections is the available format.</span>
      </p>
      <div className="grid grid-cols-3 gap-3">
        {FORMATS.map((format) => (
          <button
            key={format.id}
            type="button"
            disabled={!format.selected}
            aria-pressed={format.selected}
            className={
              format.selected
                ? 'flex min-h-[248px] flex-col rounded-md border border-[#e4e4e4] bg-[#f6f4f1] text-left'
                : 'flex min-h-[248px] cursor-not-allowed flex-col rounded-md border border-[#e4e4e4] bg-surface text-left opacity-60'
            }
          >
            <p className="px-4 pt-3 text-sm font-semibold text-foreground">{format.title}</p>
            <div className="flex flex-1 items-center justify-center px-3 py-3">{format.art}</div>
            <p className="px-4 pb-4 text-center text-xs leading-5 text-foreground-muted">
              {format.description}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}
